// frontend/src/ProtectedRoute.js
import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api, getAccessToken, clearTokens } from "./api/client";

/**
 * ProtectedRoute
 * - Ensures a user is authenticated.
 * - If `allowedRoles` is provided, ensures the user's role is included.
 *
 * Backend contract (preferred):
 *   GET /api/auth/me/
 *   {
 *     role: "educator" | "scholar" | "admin" | "user",
 *     is_educator: boolean,
 *     is_admin: boolean,          // admin capability (not necessarily Django is_staff)
 *     is_scholar: boolean,
 *     is_staff: boolean,          // Django admin-site access (optional)
 *     is_superuser: boolean
 *   }
 *
 * IMPORTANT:
 * - Educator is a role. Admin is a capability.
 * - It is possible to be an educator and not an admin.
 * - Student domain users should never satisfy educator/admin routes.
 */

function normalizeRole(val) {
  return String(val || "").trim().toUpperCase();
}

function isStudentDomainAccount(me) {
  const email = (me?.email || me?.username || "").trim().toLowerCase();
  const username = (me?.username || "").trim().toLowerCase();
  return (
    /@student\.propelschools\.org$/i.test(email) ||
    /@student\.propelschools\.org$/i.test(username)
  );
}

/**
 * Compatibility helpers:
 * - Prefer explicit booleans if present (is_educator, is_admin, is_scholar).
 * - Fall back to role string + staff flags if booleans are missing (older backend).
 */
function deriveCapabilities(me) {
  const role = normalizeRole(me?.role);

  const hasIsEducator = typeof me?.is_educator === "boolean";
  const hasIsAdmin = typeof me?.is_admin === "boolean";
  const hasIsScholar = typeof me?.is_scholar === "boolean";

  const staffFlags = !!me?.is_staff || !!me?.is_superuser;

  const isEducator =
    hasIsEducator
      ? !!me.is_educator
      : role === "EDUCATOR" ||
        role === "TEACHER" ||
        role === "STAFF" ||
        role === "ADMIN" ||
        staffFlags;

  const isAdmin =
    hasIsAdmin
      ? !!me.is_admin
      : role === "ADMIN" || staffFlags;

  const isScholar =
    hasIsScholar
      ? !!me.is_scholar
      : role === "SCHOLAR" ||
        role === "STUDENT" ||
        isStudentDomainAccount(me);

  return { role, isEducator, isAdmin, isScholar };
}

function roleSatisfiesAllowedRoles(me, allowedRoles) {
  const allowed = (allowedRoles || []).map(normalizeRole);
  const studentDomain = isStudentDomainAccount(me);

  const { role, isEducator, isAdmin, isScholar } = deriveCapabilities(me);

  // If route wants EDUCATOR → allow educator users, but never student-domain accounts
  if (allowed.includes("EDUCATOR")) {
    return isEducator && !studentDomain;
  }

  // If route wants ADMIN → require admin capability, but never student-domain accounts
  if (allowed.includes("ADMIN")) {
    return isAdmin && !studentDomain;
  }

  // If route wants SCHOLAR / STUDENT → require scholar capability
  if (allowed.includes("SCHOLAR") || allowed.includes("STUDENT")) {
    return isScholar;
  }

  // Otherwise, fallback to role string include match
  return allowed.includes(role);
}

export default function ProtectedRoute({
  children,
  allowedRoles, // optional: e.g., ['EDUCATOR'] or ['ADMIN']
  redirectTo = "/dashboard", // where to send users who don't meet role requirements
}) {
  const location = useLocation();
  const token = getAccessToken();

  const needsRoleCheck = Array.isArray(allowedRoles) && allowedRoles.length > 0;

  const [status, setStatus] = useState({
    loading: needsRoleCheck,
    allowed: !needsRoleCheck,
  });

  useEffect(() => {
    let mounted = true;
    if (!needsRoleCheck) return;

    (async () => {
      try {
        // ✅ Always use trailing slash for Django endpoints
        const res = await api.get("/api/auth/me/");

        if (!mounted) return;

        if (res?.ok) {
          const me = res.data || {};
          const ok = roleSatisfiesAllowedRoles(me, allowedRoles);

          setStatus({ loading: false, allowed: ok });

          const caps = deriveCapabilities(me);

          // eslint-disable-next-line no-console
          console.debug("[guard] /me:", {
            role: me?.role,
            email: me?.email,
            username: me?.username,
            is_staff: !!me?.is_staff,
            is_superuser: !!me?.is_superuser,
            is_educator: typeof me?.is_educator === "boolean" ? !!me?.is_educator : undefined,
            is_admin: typeof me?.is_admin === "boolean" ? !!me?.is_admin : undefined,
            is_scholar: typeof me?.is_scholar === "boolean" ? !!me?.is_scholar : undefined,
            derived: caps,
            needsRoleCheck,
            allowedRoles,
            allowed: ok,
            studentDomain: isStudentDomainAccount(me),
          });
        } else {
          clearTokens?.();
          setStatus({ loading: false, allowed: false });
        }
      } catch {
        clearTokens?.();
        if (mounted) setStatus({ loading: false, allowed: false });
      }
    })();

    return () => {
      mounted = false;
    };
  }, [needsRoleCheck, allowedRoles]);

  // eslint-disable-next-line no-console
  console.debug(
    "[guard] path:",
    location.pathname,
    "hasToken:",
    !!token,
    "needsRoleCheck:",
    needsRoleCheck
  );

  // 1) Not logged in → login
  if (!token) {
    // eslint-disable-next-line no-console
    console.debug("[guard] redirect → /login from", location.pathname);
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // 2) Still checking role → render nothing to avoid flicker
  if (status.loading) return null;

  // 3) Role not allowed
  if (needsRoleCheck && !status.allowed) {
    // eslint-disable-next-line no-console
    console.debug(
      "[guard] role not allowed →",
      redirectTo,
      "from",
      location.pathname
    );
    return <Navigate to={redirectTo} replace />;
  }

  // 4) Allowed
  return children;
}
